import React, {
  PropsWithChildren, ReactElement, cloneElement, useCallback, useEffect, useState,
} from 'react';
import { InjectedFormProps, reduxForm } from 'redux-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as R from 'ramda';
import { omit } from 'lodash';

import Modal from '@common/components/modal';
import { Checkbox } from '@common/components/form/inputs/checkbox';
import Icon from '@common/components/icon';
import UsersInput from '@common/components/form/inputs/users';
import Label from '@common/components/form/label';
import AsyncList from '@common/components/list/async';
import { Button } from '@common/components/button';
import * as Alert from '@common/services/alert';
import RoleRow from '@modules/admin/components/role-row';

import { updateUserRoles } from '../../actions';
import { ExtendedUser, Network, Organisation, UIRole } from '@common/types/objects';
import { ELevels } from '@common/definitions';

type FormState = {
  admins: { add: string[]; remove: string[] };
  roles: string[];
};

type Admin = ExtendedUser & { roles: (UIRole)[] };
type AdminLookup = Record<string, Admin>;

interface AdminsFormOwnProps extends PropsWithChildren<{}> {
  roles: UIRole[];
  organisation?: Organisation;
  network?: Network;
  admins: Admin[];
  fetchGroups: (level: number) => any;
  onSuccess?: () => Promise<void>;
  updateAdmin?: (
    userId: string,
    payload: { add?: Array<string>, remove?: Array<string> },
    networkId?: string,
    roles?: UIRole[],
  ) => any;
}

type ReduxFormProps = InjectedFormProps<FormState, AdminsFormOwnProps, boolean>;
type AdminsFormProps = AdminsFormOwnProps & ReduxFormProps;

const AdminsForm = ({
  roles, organisation, network, admins, fetchGroups, submitting, children,
  handleSubmit, updateAdmin = updateUserRoles, onSuccess, reset,
}: AdminsFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [step, setStep] = useState(0);
  const [selectedRoles, setSelectedRoles] = useState<Record<string, UIRole>>({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setSelectedRoles({});
      reset();
      setStep(0);
    }
  }, [setSelectedRoles, setStep, isVisible, reset]);

  const handleToggleRole = useCallback((role: UIRole) => {
    if (selectedRoles[role.id]) {
      setSelectedRoles(omit(selectedRoles, [role.id]));
    } else {
      setSelectedRoles({
        ...selectedRoles,
        [role.id]: role,
      });
    }
  }, [selectedRoles, setSelectedRoles]);

  const onSubmit = handleSubmit(async (values: FormState) => {
    const payload = { add: Object.keys(selectedRoles) };
    try {
      await Promise.all(values.admins.add.map((id) => {
        return dispatch(updateAdmin(id, payload, network?.id, roles));
      }));
      Alert.success(t('organisation:forms_admins_success'));
      if (onSuccess) await onSuccess();
    } catch (error: any) {
      Alert.forStatus(error.status_code, {
        warning: t('organisation:forms_admins_warning'),
        error: t('organisation:forms_admins_error'),
      });
    } finally {
      setIsVisible(false);
    }
  });

  const adminLookup: AdminLookup = admins.reduce((acc, admin) => ({ ...acc, [admin.id]: admin }), {});

  // TODO: use lodash instead of ramda
  // Get all permissions from the selected roles and take the unique values
  const selectedPermissions = R.uniq(R.flatten(R.values(R.pluck('permissions' as any, selectedRoles as any))));

  return (
    <Modal
      show={isVisible}
      onClose={() => setIsVisible(false)}
      size={step === 1 ? 'large' : undefined}
      title={t(network ? 'network:forms_admins_title' : 'organisation:forms_admins_title')}
      wrapper={Modal.FormWrapper}
      wrapperProps={{ onSubmit }}
      content={step === 1 ? (
        <div className="RoleSelector">
          <div className="RoleSelector__Roles">
            {roles.map((role) => (
              <div
                key={role.id}
                className="RoleSelector__Roles__Item"
                onClick={() => handleToggleRole(role)}
              >
                <Checkbox size="large" value={!!selectedRoles[role.id]} />
                {role.name}
                {role.is_global && <Icon type="star__filled" className="GlobalRole__Icon" />}
              </div>
            ))}
          </div>
          <div className="RoleSelector__Permissions">
            <AsyncList
              data={{
                onFetch: () => fetchGroups(network ? ELevels.NETWORK : ELevels.ORGANISATION),
                useCursor: undefined,
              }}
              rowProps={{ selectedPermissions }}
              renderRow={RoleRow}
            />
          </div>
        </div>
      ) : (
        <>
          <Label text={t('organisation:forms_admins_select_users')} />
          <UsersInput
            name="admins"
            organisation={organisation}
            network={network}
            isUserSelected={() => false}
            isDisabled={(item: Admin) => {
              if (!adminLookup[item.id]) return false;
              return adminLookup[item.id].roles.some((role: UIRole) => role.is_global);
            }}
          />
        </>
      )}
      onBack={step === 1 ? (() => setStep(0)) : undefined}
      footer={step === 1 ? (
        <Button
          key="submit"
          type="primary"
          isLoading={submitting}
          buttonType="submit"
        >
          {t('organisation:forms_admins_edit')}
        </Button>
      ) : (
        <Button
          key="next"
          type="primary"
          onClick={() => setStep(1)}
        >
          {t('organisation:forms_admins_select')}
        </Button>
      )}
    >
      {children && cloneElement(children as ReactElement, { onClick: () => setIsVisible(!isVisible) })}
    </Modal>
  );
};

export default reduxForm<FormState, AdminsFormOwnProps, boolean>({
  form: 'organisation-admins',
  initialValues: {
    admins: { add: [], remove: [] },
    roles: [],
  },
})(AdminsForm);
